import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewRef, inject } from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {IntersectionState, LazyLoadService} from '../lazyload/index';
import {Subscription} from 'rxjs';
import {Product} from '../../classes/product';
import {ShoppingCartService} from '../../service/shoppingcart.service';
import {ActiveOrderService} from '../../service/active.order.service';
import {UserService} from '../../service/user.service';
import {ProductService} from '../../service/product.service';
import {ShoppingCartLine} from '../../classes/shoppingcartline';
import {ProductItemCommonComponent} from './productitem.common.component';
import {ProductPrices} from '../../classes/product.prices';
import {ConfigurationHelper} from '../../helper/configuration.helper';
import {BrowserService} from '../../service/browser.service';
import {TagManagerService} from '../../service/tag.manager.service';
import {NumberFormatPipe} from '../../pipes/number.format.pipe';
import {RenderCheckoutIconComponent} from '../cart/render.checkout.icon';
import {NgClass} from '@angular/common';
import {NgSelectModule} from '@ng-select/ng-select';
import {ProductSizeComponent} from './product.size.component';
import {ProductItemFavoriteComponent} from './productitem.favorite.component';
import {RenderProductImageComponent} from './render.product.image';
import {CustomDatePipe} from '../../pipes/custom.date.pipe';
import {ProductMoreAvailableComponent} from './product.more.available.component';
import {ProductQuantityUtils} from '../../utils/product.quantity.utils';

interface AvailableUnit {
	price: number;
	qty: number;
	qtyBox?: number;
	value: string;
}

/**
 * This component has animations. When adding a product, the state changes from normal to added. After a
 * timeout of 2 seconds, it animates back to the normal state.
 *
 * @author evandongen
 */
@Component({
	selector: 'product-item-grid',
	templateUrl: 'productitem.grid.component.html',
	standalone: true,
	imports: [
		RenderProductImageComponent,
		ProductItemFavoriteComponent,
		ProductSizeComponent,
		ProductMoreAvailableComponent,
		FormsModule,
		ReactiveFormsModule,
		NgSelectModule,
		NgClass,
		RenderCheckoutIconComponent,
		NumberFormatPipe,
		CustomDatePipe,
	],
})
export class ProductItemGridComponent extends ProductItemCommonComponent implements OnInit, OnDestroy, AfterViewInit {
	private lazyLoadService = inject(LazyLoadService);
	private shoppingCartService = inject(ShoppingCartService);
	private formBuilder = inject(UntypedFormBuilder);
	private activeOrderService = inject(ActiveOrderService);
	private userService = inject(UserService);
	private changeDetectorRef = inject(ChangeDetectorRef);
	private tagManagerService = inject(TagManagerService);
	private productService = inject(ProductService);

	// Unit Types
	static readonly UNIT_VALUE_TYPE = 'unit';
	static readonly TRAY_VALUE_TYPE = 'tray';
	static readonly LAYER_VALUE_TYPE = 'layer';
	static readonly TROLLEY_VALUE_TYPE = 'trolley';

	@Input() product: Product;
	@Input() showMoreInfoButton: boolean = false;
	@Input() loggedIn: boolean;
	@Input() useFastCheckout: boolean;
	@Input() scrollToId: string;
	@Input() userCurrency: string;
	@Input() buttonsDisabled: boolean;
	@Input() planthunterId: string = null;
	@Input() gridRowHasStockTypes: boolean;
	@Input() gridRowHasSaleProducts: boolean;
	@Output() productMatchedFragmentEvent = new EventEmitter();
	@Output() viewProductDetailClickEvent = new EventEmitter();

	form: UntypedFormGroup;
	priceBasedOnSelectedUnit: number;
	priceBasedOnSelectedUnitWithDiscount: number;
	trolleyPriceWithDiscount: number;
	quantityValue: number;
	minimumOrderQuantity: number;
	maxValue: number;
	selectedUnit: string;
	itemsPerUnit: number;
	itemsPerUnitMax: number;
	itemsInOrder: number = 0;
	buttonShowPressed: boolean = false;
	showConfirmation: boolean = false;
	showProduct: boolean = false;
	availableUnitOptions: AvailableUnit[];
	productPrices: ProductPrices;

	private shoppingcartSubscription: Subscription;

	doLoad(state: IntersectionState) {
		this.showProduct = true;
	}

	triggerProductMatchedFragmentEvent(anchorValue: string) {
		this.productMatchedFragmentEvent.emit(anchorValue);
	}

	getProduct(): Product {
		return this.product;
	}

	getProductService() {
		return this.productService;
	}

	getScrollToId() {
		return this.scrollToId;
	}

	getImageAltTitle() {
		const productImageText = $localize`:@@product.image.alt.suffix:product image`;

		if (this.product.producer) {
			return this.productService.trimWhiteSpaceForProductDescriptions(
				`${this.product.totalDescription1} ${this.product.producer}`,
			);
		}

		return this.productService.trimWhiteSpaceForProductDescriptions(
			`${this.product.totalDescription1} ${productImageText}`,
		);
	}

	ngOnDestroy(): void {
		if (this.shoppingcartSubscription) {
			this.shoppingcartSubscription.unsubscribe();
		}

		this.changeDetectorRef.detach();
	}

	isFastCheckout(): boolean {
		return this.userService.isUseFastCheckout();
	}

	/**
	 * Check if the product is loaded on the website by matching the url to '/site/{lang}/'
	 */
	isWebsite(): boolean {
		const location = ProductItemCommonComponent.winRef.nativeWindow.location.pathname;
		const regexLocation = /^\/(site)\/([a-z]{2})\//gm;

		return location.match(regexLocation);
	}

	ngAfterViewInit() {
		super.ngAfterViewInit();
	}

	ngOnInit() {
		super.ngOnInit();

		if (BrowserService.isEdgeBrowser() || this.isWebsite()) {
			this.showProduct = true;
		} else {
			this.lazyLoadService.isDevMode = false;
			this.lazyLoadService.loadAheadCount = 10;
		}

		this.form = this.formBuilder.group({
			numberOfUnits: ['', Validators.required],
			unit: [this.product.qtyByBox, Validators.required],
		});

		if (this.loggedIn) {
			this.shoppingcartSubscription = this.shoppingCartService
				.getShoppingCartObservable()
				.subscribe((lines: ShoppingCartLine[]) => this.updateItemsInOrder(lines));
		}

		this.productPrices = new ProductPrices(this.product, this.itemsInOrder);

		if (this.productPrices.showUnitOption()) {
			this.selectedUnit = 'unit';
		}

		if (this.productPrices.showTrayOption()) {
			this.selectedUnit = 'tray';
		}

		this.setPriceAndItemsPerUnitBasedOnSelectedUnit();

		this.trolleyPriceWithDiscount = this.product.trolleyDiscountPrice;

		this.availableUnitOptions = this.createAvailableUnitOptions();

		this.form.get('unit')?.setValue(this.selectedUnit);
	}

	onSelectedUnitChange($event) {
		this.selectedUnit = $event.value;

		this.setPriceAndItemsPerUnitBasedOnSelectedUnit();
		this.changeQuantity();
	}

	isEnabled(): boolean {
		const maximumOrderQuantity = this.getMaximumOrderQuantity();
		return maximumOrderQuantity === null || this.minimumOrderQuantity <= maximumOrderQuantity;
	}

	getAvailableQuantity(): number {
		return ProductQuantityUtils.getAvailableBoxQuantity(this.product, this.shoppingCartService.activeShoppingCartLines);
	}

	/**
	 * Checks if the combination of quantity and order unit is not less than the minimum quantity or
	 * larger than the available quantity.
	 */
	changeQuantity() {
		const minimum = this.minimumOrderQuantity;

		if (minimum > this.quantityValue) {
			// Can not order less than minimum.
			this.setQuantityValue(minimum);
			return;
		}

		const maximum = this.getMaximumOrderQuantity();

		if (maximum !== null && maximum > 0 && this.quantityValue > maximum) {
			// Can not order more than maximum.
			this.setQuantityValue(maximum);
			return;
		}
	}

	/**
	 * @param value
	 */
	onSubmit(value: any) {
		if (this.buttonShowPressed || this.form.invalid) {
			return;
		}

		const maximumOrderQuantity = this.getMaximumOrderQuantity();
		if (maximumOrderQuantity && value.numberOfUnits > maximumOrderQuantity) {
			return;
		}

		const numberOfItemsToOrder: number = value.numberOfUnits * this.itemsPerUnit;

		// Don't show the spinner if the user is first to select an order in the popup.
		if (this.activeOrderService.hasSelectedOrder()) {
			this.buttonShowPressed = true;
			// Because some parts of the component are rendered from a value (show) that changes in the parent we need
			// to detect the changes in the child too since the parent uses the onPush strategy.
			// For all other interactivity inside this specific component that don't rely on parent values we do not
			// need to detect the changes
			this.changeDetectorRef.detectChanges();
		}

		const productOrderDone = this.shoppingCartService.orderProduct(
			this.product,
			numberOfItemsToOrder,
			false
		);

		// Can be null if the user had to select a (new) active order.
		if (productOrderDone == null) {
			this.buttonShowPressed = false;
			return;
		}

		productOrderDone.subscribe(
			() => {
				if (this.buttonShowPressed === true) {
					this.showConfirmation = true;
					this.setQuantityValue(this.minimumOrderQuantity);
					this.productService.triggerProductAddedToCartEvent(this.product);

					this.changeDetectorRef.detectChanges();

					setTimeout(() => {
						this.showConfirmation = false;
						this.buttonShowPressed = false;

						// Because we do this change detection in a timeout we need to first check
						// if the view has been destroyed at all so we don't try to detect changes
						// on a view that might be destroyed by angular through for example navigation
						// TODO: SUPNOVICOM-901 Extend ChangeDetectorRef code to allow for simple view destruction checking
						if (!(this.changeDetectorRef as ViewRef).destroyed) {
							this.changeDetectorRef.detectChanges();
						}
					}, 2000);
				}
			},
			() => {
				this.buttonShowPressed = false;
				this.changeDetectorRef.detectChanges();
			},
		);
	}

	/**
	 * Checks if the current url is the products page.
	 *
	 * @returns {boolean}
	 */
	isWebshopProductsUrl(): boolean {
		return (
			window.location.href.indexOf(
				ConfigurationHelper.getWebshopBaseUrl(),
			) > -1 && window.location.href.indexOf('products') > -1
		);
	}

	/**
	 * Redirects to the product detail page but sets a browser state before the actual routing.
	 */
	viewProductDetail() {
		const productDetailUrl = this.productService.getProductDetailUrl(this.product);
		this.tagManagerService.trackProductViewClick(this.product);

		// This is a really nasty workaround for the CTRL/CMD click open in new tab functionality
		// We sadly need to do this because normally we also set some values in the browser which do
		// not need to be added at this time.
		if (ProductItemCommonComponent.winRef.nativeWindow.openInNewTab) {
			return window.open(this.getProductDetailUrl());
		}

		// Only 'push' the product state if you navigate from the webshop products page to be able to go 'back'
		// to a specific #anchor in the page.
		if (this.isWebshopProductsUrl()) {
			this.productService.pushProductState(this.product);
		} else {
			const location = ProductItemGridComponent.winRef.nativeWindow.location.pathname;
			this.productService.pushWebsiteProductState(this.product, location, this.planthunterId);
		}

		return this.viewProductDetailClickEvent.emit(productDetailUrl);
	}

	getProductDetailUrl() {
		return this.productService.getProductDetailUrl(this.product);
	}

	/**
	 * Get the height for the product-size component, this wil vary if the grid row has a sales product.
	 * @return {number} height or 0=auto height
	 */
	productSizeComponentHeight(): number {
		if (this.gridRowHasSaleProducts) {
			return 37;
		}
		return 0;
	}

	private setPriceAndItemsPerUnitBasedOnSelectedUnit() {
		if (
			this.selectedUnit === ProductItemGridComponent.UNIT_VALUE_TYPE ||
			this.selectedUnit === ProductItemGridComponent.TRAY_VALUE_TYPE
		) {
			this.itemsPerUnit = this.product.qtyByBox;
			this.itemsPerUnitMax = this.product.qtyByBox;
			this.priceBasedOnSelectedUnit = this.product.unitPrice;
			this.priceBasedOnSelectedUnitWithDiscount = this.product.unitDiscountPrice;
		}

		if (this.selectedUnit === ProductItemGridComponent.LAYER_VALUE_TYPE) {
			this.itemsPerUnit = this.product.qtyByBox * this.product.qtyPackingsByLayer;
			this.itemsPerUnitMax = this.product.qtyByBox * this.product.qtyPackingsByLayer;
			this.priceBasedOnSelectedUnit = this.product.layerPrice;
			this.priceBasedOnSelectedUnitWithDiscount = this.product.layerDiscountPrice;
		}

		if (this.selectedUnit === ProductItemGridComponent.TROLLEY_VALUE_TYPE) {
			this.itemsPerUnit = this.product.qtyByTrolley;
			this.itemsPerUnitMax = this.product.qtyByTrolley;
			this.priceBasedOnSelectedUnit = this.product.trolleyPrice;
			this.priceBasedOnSelectedUnitWithDiscount = this.product.trolleyDiscountPrice;
		}

		this.minimumOrderQuantity = this.getMinimumOrderQuantity();
		this.maxValue = this.getMaximumOrderQuantity();
		this.setQuantityValue(this.minimumOrderQuantity);
	}

	/**
	 * Sets the quantity value of the form input element
	 */
	private setQuantityValue(newQuantity: number) {
		this.quantityValue = newQuantity;
		this.form.get('numberOfUnits')?.setValue(this.quantityValue);
	}

	private getMinimumOrderQuantity(): number {
		return ProductQuantityUtils.getMinOrderQuantity(this.product, this.itemsPerUnit);
	}

	private getMaximumOrderQuantity(): number {
		return ProductQuantityUtils.getMaxOrderQuantity(this.itemsPerUnit, this.product, this.itemsInOrder);
	}

	private updateItemsInOrder(lines: ShoppingCartLine[]) {
		if (lines) {
			const activeLine = lines.find(line =>
				line.priceListLineNo === this.product.priceListLineNo &&
				line.priceListNo === this.product.priceListNo,
			);

			if (activeLine && (activeLine.shoppingCartMode || this.shoppingCartService.isShoppingCartLineEditable(activeLine))) {
				this.itemsInOrder = activeLine.quantity;
				this.maxValue = this.getMaximumOrderQuantity();
				return;
			}
		}

		this.itemsInOrder = 0;
		this.maxValue = this.getMaximumOrderQuantity();
	}

	/**
	 * Create an Array for the price select box per product. Each product has their own prices and quantities
	 * @return {Array}
	 */
	private createAvailableUnitOptions(): AvailableUnit[] {
		// Initialise an empty array
		const optionsArray = [];
		let optionObject = {};

		// Create the array based on availability
		if (this.productPrices.showUnitOption()) {
			optionObject = {
				value: ProductItemGridComponent.UNIT_VALUE_TYPE,
				qty: this.product.qtyByBox,
				price: this.productPrices.getUnitOrTrayNormalOrDiscountPrice(),
			};
			optionsArray.push(optionObject);
		}

		if (this.productPrices.showTrayOption()) {
			optionObject = {
				value: ProductItemGridComponent.TRAY_VALUE_TYPE,
				qty: this.product.qtyByBox,
				price: this.productPrices.getUnitOrTrayNormalOrDiscountPrice(),
			};
			optionsArray.push(optionObject);
		}

		if (this.productPrices.showLayerOption()) {
			optionObject = {
				value: ProductItemGridComponent.LAYER_VALUE_TYPE,
				qty: this.product.qtyPackingsByLayer,
				qtyBox: this.product.qtyByBox,
				price: this.productPrices.getLayerNormalOrDiscountPrice(),
			};
			optionsArray.push(optionObject);
		}

		if (this.productPrices.showTrolleyOption()) {
			optionObject = {
				value: ProductItemGridComponent.TROLLEY_VALUE_TYPE,
				qty: this.product.qtyPackingsByTrolley,
				qtyBox: this.product.qtyByBox,
				price: this.productPrices.getTrolleyNormalOrDiscountPrice(),
			};
			optionsArray.push(optionObject);
		}

		return optionsArray;
	}
}
